import { useCallback, useEffect, useRef, useState } from 'react'
import { listCameraLucidaInquiries } from '../lib/cameraLucidaPersistence'
import type { CameraLucidaPersistedInquiry } from '../types/cameraLucidaTypes'

export interface CameraLucidaInquiryHistoryState {
  inquiries: CameraLucidaPersistedInquiry[]
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

interface CameraLucidaInquiryHistoryLoadState {
  chapterId?: string
  inquiries: CameraLucidaPersistedInquiry[]
  loading: boolean
  error: string | null
}

export const useCameraLucidaInquiryHistory = (
  chapterId?: string,
): CameraLucidaInquiryHistoryState => {
  const [loadState, setLoadState] = useState<CameraLucidaInquiryHistoryLoadState>({
    chapterId,
    inquiries: [],
    loading: Boolean(chapterId),
    error: null,
  })
  const requestVersionRef = useRef(0)

  const refresh = useCallback(async () => {
    const requestVersion = ++requestVersionRef.current
    if (!chapterId) {
      setLoadState({ chapterId: undefined, inquiries: [], loading: false, error: null })
      return
    }

    setLoadState(previous => ({
      chapterId,
      inquiries: previous.chapterId === chapterId ? previous.inquiries : [],
      loading: true,
      error: null,
    }))
    try {
      const records = await listCameraLucidaInquiries(chapterId)
      if (requestVersionRef.current === requestVersion) {
        setLoadState({ chapterId, inquiries: records, loading: false, error: null })
      }
    } catch (refreshError) {
      if (requestVersionRef.current === requestVersion) {
        setLoadState(previous => ({
          ...previous,
          chapterId,
          loading: false,
          error: refreshError instanceof Error ? refreshError.message : 'Could not load Camera Lucida inquiries',
        }))
      }
    }
  }, [chapterId])

  useEffect(() => {
    void refresh()
    return () => {
      requestVersionRef.current += 1
    }
  }, [refresh])

  const scopeMatches = loadState.chapterId === chapterId
  return {
    inquiries: scopeMatches ? loadState.inquiries : [],
    loading: Boolean(chapterId) && (!scopeMatches || loadState.loading),
    error: scopeMatches ? loadState.error : null,
    refresh,
  }
}
